import { useCallback, useEffect, useRef, useState } from 'react';

import { faWaveformLines } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Toast,
} from '@repo/videa-components/src/index';
import { Toaster } from '@repo/videa-components/src/ui/sonner';
import {
  ProviderSelectionDialog,
  RecordingRecoveryModal,
  monitoringStreamAtom,
  noteGenerationStepAtom,
  selectedLanguageAtom,
  silenceSeverityAtom,
  useMicrophoneContext,
  useNavigationBlock,
  useRecoveryCheck,
} from '@repo/videa-notes';
import axios from 'axios';
import { toast } from 'sonner';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { shallowEqual, useSelector } from 'react-redux';

import { useHasWriteback } from '@/react-query/videa-notes/useHasWriteback';
import { useDevtoolsShortcut } from '@/utils/hooks/useDevtoolsShortcut';
import { formatPatientFirstNameAndLastName } from '@/utils/patient';
import { sendException } from '@/utils/sentry';
import { createDebugLogger } from '@videa/utils';

import { usePreferences } from '../../react-query/usePreferences';
import { mixpanelEvents } from '../../utils/mixpanel';
import { disableKeyPressAtom } from '../annotator/casePresentation/atoms';
import { selectDateAtom } from '../patientList/atoms';

import { VideaNotesMainContent } from './components/VideaNotesMainContent';
import { VideaNotesCoreProvider } from './context/VideaNotesCoreContext';
import { VideaNotesNoteResultProvider } from './context/VideaNotesNoteResultContext';
import { VideaNotesRecordingProvider } from './context/VideaNotesRecordingContext';
import { useAppointmentData } from './hooks/useAppointmentData';
import { useAppointmentNotes } from './hooks/useAppointmentNotes';
import { type UploadErrorMeta, useAudioUpload } from './hooks/useAudioUpload';
import { useNoteManagement } from './hooks/useNoteManagement';
import { usePatientConsent } from './hooks/usePatientConsent';
import { usePromptTemplateSelection } from './hooks/usePromptTemplateSelection';
import { useSaveNoteToPMS } from './hooks/useSaveNoteToPMS';
import { useSynapseUploadIntegration } from './hooks/useSynapseUploadIntegration';
import { useVideaNotesAnalytics } from './hooks/useVideaNotesAnalytics';
import { useVideaNotesUIAtoms } from './hooks/useVideaNotesAtoms';
import {
  useCoreContextValue,
  useNoteResultContextValue,
  useRecordingContextValue,
} from './hooks/useVideaNotesContextValues';
import { useVideaNotesEventHandlers } from './hooks/useVideaNotesEventHandlers';
import { VideaNotesUIState } from './types';

const debug = createDebugLogger('VideaNotesSheet');

export const VideaNotesSheet = () => {
  // Redux selectors
  const { currentPracticeId, currentOrganizationId, displayPhi, username } =
    useSelector(
      (state: RootStateOrAny) => ({
        currentPracticeId: state?.login?.user?.primaryPracticeId,
        currentOrganizationId: state?.login?.user?.primaryOrganizationId,
        displayPhi: state?.login?.user?.privacyOn,
        username: state?.login?.user?.username,
      }),
      shallowEqual,
    );

  // Feature flags/preferences
  const { data: preferences } = usePreferences();
  const isVideaNotesEnabled = preferences?.isVideaNotesEnabled;
  const supportedLanguages = preferences?.ambientNotesLanguageSupport ?? [
    'en-US',
  ];
  const [selectedLanguage, setSelectedLanguage] = useAtom(selectedLanguageAtom);

  // Check for recoverable sessions on mount
  useRecoveryCheck();

  // Ref to hold startAmbientSession - avoids hook ordering issues
  const startAmbientSessionRef = useRef<
    (options?: {
      consentRecordId?: string;
      opfsSessionId?: string;
      recoveredAmbientSessionId?: string;
    }) => Promise<string | null>
  >(() => Promise.resolve(null));

  // Dev tools shortcut (r+b+l) for hidden features
  const { showDevtools } = useDevtoolsShortcut();

  // Grouped UI atom hook
  const {
    uiStatus,
    setUiStatus,
    isSheetOpen,
    setIsSheetOpen,
    isCancelRecordingConfirmationShown,
    setIsCancelRecordingConfirmationShown,
    voiceNotesSource,
    recorderStatus,
    mediaBlobUrl,
  } = useVideaNotesUIAtoms();
  const setNoteGenerationStep = useSetAtom(noteGenerationStepAtom);

  // Navigation blocking during recording/upload
  useNavigationBlock();

  // Monitoring stream setter (only functional microphone atom needed here)
  const setMonitoringStream = useSetAtom(monitoringStreamAtom);

  // Disable keypress when sheet is open
  const setDisableKeyPress = useSetAtom(disableKeyPressAtom);

  // Silence severity for pulsing red border on early silence
  const silenceSeverity = useAtomValue(silenceSeverityAtom);

  // Audio device state from microphone context
  const {
    devices: availableAudioDevices,
    selectedDeviceId: selectedAudioDeviceId,
  } = useMicrophoneContext();

  // Disable keypress when the voice notes sheet is open
  useEffect(() => {
    setDisableKeyPress(isSheetOpen);
    return () => setDisableKeyPress(false);
  }, [isSheetOpen, setDisableKeyPress]);

  // Appointment data hook
  const {
    patient,
    patientName,
    patientNameForConsentRecord,
    estimatedVisitId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    appointmentStartsAt,
    setAppointmentData: setAppointmentDataForVideaNotes,
  } = useAppointmentData(displayPhi);

  // Template selection hook
  const { templateOptions, selectedTemplate, setSelectedTemplate } =
    usePromptTemplateSelection();

  // Compute selected template details
  const selectedTemplateOption = templateOptions.find(
    (t) => t.id === selectedTemplate,
  );
  const selectedTemplateName = selectedTemplateOption?.name ?? null;
  const selectedTemplateUserInstructions =
    selectedTemplateOption?.userInstructions ?? null;

  // External data
  const selectedDate = useAtomValue(selectDateAtom);
  const hasWriteback = useHasWriteback(
    currentPracticeId,
    currentOrganizationId,
    isSheetOpen,
  );
  const hasWritebackEnabled = Boolean(hasWriteback?.data);

  // Custom hooks for feature logic
  const {
    clinicalNotesData,
    isLoading: isLoadingNotes,
    error: notesError,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
  } = useAppointmentNotes({
    unifiedAppointmentId,
    standalonePatientVisitId,
    isSheetOpen,
  });

  const {
    selectedNote,
    noteContent,
    patientConsentBlurb,
    variableDefinitions,
    hasBeenEdited,
    isSavingNoteUpdate,
    isNoteSavedToPMS,
    isNoteBeingSavedToPMS,
    isNoteReadOnly,
    editedFields,
    handleSetNoteContent,
    handleSetSelectedNoteId,
    handleSaveNoteBeforeClose,
    handleResetNoteToOriginal,
    handleVariableValueChange,
  } = useNoteManagement({
    clinicalNotesData,
    hasWritebackEnabled,
    enableVoiceNotesConsent: preferences?.enableVoiceNotesConsent ?? false,
  });

  // Analytics hook (builds payload internally, reads microphone atoms internally)
  const { trackVideaNotesEvent } = useVideaNotesAnalytics({
    patient,
    estimatedVisitId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    selectedTemplateId: selectedTemplate,
    selectedTemplateName,
    selectedDate,
    voiceNotesSource,
  });

  // Patient consent hook
  const {
    patientConsentRecordId,
    handleStartRecording: handleStartRecordingConsent,
    handleStartProviderOnlyRecording: handleStartProviderOnlyRecordingConsent,
    handlePatientConsentDenied: handlePatientConsentDeniedConsent,
  } = usePatientConsent({
    patient,
    patientNameForConsentRecord,
    appointmentStartsAt,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    username,
    enableVoiceNotesConsent: preferences?.enableVoiceNotesConsent ?? false,
    onStartRecordingSuccess: (consentRecordId) => {
      trackVideaNotesEvent(mixpanelEvents.videaNotesStartRecorderClick);
      // Start Synapse ambient session when recording begins.
      // Pass the consent ID directly to avoid React state timing issues.
      // Note: opfsSessionId may not be available yet - the useEffect in useSynapseAudioUpload
      // will handle registering with the SW once it's available.
      startAmbientSessionRef.current({ consentRecordId }).catch((error) => {
        debug.error('Failed to start ambient session:', error);
      });
    },
    onStartProviderOnlyRecordingSuccess: (consentRecordId) => {
      trackVideaNotesEvent(
        mixpanelEvents.videaNotesStartProviderOnlyRecorderClick,
      );
      // Start Synapse ambient session when recording begins.
      startAmbientSessionRef.current({ consentRecordId }).catch((error) => {
        debug.error('Failed to start ambient session:', error);
      });
    },
    onPatientConsentDeniedSuccess: () =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesPatientConsentDenied),
  });

  // Audio upload hook (legacy flow via nucleus-api)
  const {
    processingError: legacyProcessingError,
    uploadAudio: legacyUploadAudio,
  } = useAudioUpload({
    mediaBlobUrl,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    patientConsentRecordId,
    selectedTemplate,
    selectedLanguage,
    enableVoiceNotesConsent: preferences?.enableVoiceNotesConsent ?? false,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
    onSetUiStatus: setUiStatus,
    onSetIsSheetOpen: setIsSheetOpen,
    onSetSelectedNoteId: handleSetSelectedNoteId,
    onUploadError: (errorMeta: UploadErrorMeta) =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesGenerateNotesError, {
        errorStep: errorMeta.step,
        errorStatusCode: errorMeta.statusCode,
        errorMessage: errorMeta.errorMessage,
        errorCode: errorMeta.errorCode,
        errorUrl: errorMeta.url,
      }),
    onUploadSuccess: (s3UploadTime) =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesS3UploadTime, {
        s3UploadTime,
        uploadFlow: 'legacy',
      }),
    onGenerateStartInitiateUploadRequest: () =>
      trackVideaNotesEvent(
        mixpanelEvents.videaNotesProcessingRequestStartInitiateUpload,
      ),
    onGenerateStartPresignedUrlsRequest: () =>
      trackVideaNotesEvent(
        mixpanelEvents.videaNotesProcessingRequestStartPresignedUrls,
      ),
    onGenerateStartUploadPartsRequest: () =>
      trackVideaNotesEvent(
        mixpanelEvents.videaNotesProcessingRequestStartUploadParts,
      ),
    onGenerateStartCompleteUploadRequest: () =>
      trackVideaNotesEvent(
        mixpanelEvents.videaNotesProcessingRequestStartCompleteUpload,
      ),
    onGenerateSuccess: (generationTime) =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesResultLoadTime, {
        loadTime: generationTime,
        uploadFlow: 'legacy',
      }),
  });

  // Synapse async upload integration (feature-flagged)
  const {
    processingError: synapseProcessingError,
    startAmbientSession,
    finishRecording: synapseFinishRecording,
    uploadFileViaSynapse: synapseUploadFile,
    isConfigured: isSynapseConfigured,
  } = useSynapseUploadIntegration({
    unifiedAppointmentId,
    standalonePatientVisitId,
    estimatedVisitId,
    externalPatientId: patient?.sourcePatientId,
    pmsPatientId: patient?.pmsPatientId,
    imagingPatientId: patient?.imagingPatientId,
    selectedLanguage,
    patientName:
      formatPatientFirstNameAndLastName(
        patient?.firstName,
        patient?.lastName,
      ) ?? undefined,
    currentOrganizationId,
    currentPracticeId,
    patientConsentRecordId,
    selectedPrompt: selectedTemplate, // Using template as prompt for now
    selectedTemplate,
    availableAudioDevices,
    selectedAudioDeviceId: selectedAudioDeviceId ?? undefined,
    enableVoiceNotesConsent: preferences?.enableVoiceNotesConsent ?? false,
    enableVoiceNotesCustomTemplates: true, // Always use custom templates via templateId
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
    setUiStatus,
    setIsSheetOpen,
    onSetSelectedNoteId: handleSetSelectedNoteId,
    onGenerateSuccess: (generationTime) =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesResultLoadTime, {
        loadTime: generationTime,
        uploadFlow: 'synapse',
      }),
    onUploadError: (errorMeta: UploadErrorMeta) => {
      trackVideaNotesEvent(mixpanelEvents.videaNotesGenerateNotesError, {
        errorStep: errorMeta.step,
        errorStatusCode: errorMeta.statusCode,
        errorMessage: errorMeta.errorMessage,
        errorCode: errorMeta.errorCode,
        errorUrl: errorMeta.url,
        uploadFlow: 'synapse',
      });
      sendException(
        new Error(
          `Voice notes upload error [synapse]: ${errorMeta.errorMessage}`,
        ),
        {
          tags: [
            ['action', 'VOICE_NOTES_UPLOAD_ERROR'],
            ['uploadFlow', 'synapse'],
            ['errorStep', errorMeta.step],
          ],
          extras: {
            step: errorMeta.step,
            statusCode: errorMeta.statusCode,
            errorCode: errorMeta.errorCode,
            errorMessage: errorMeta.errorMessage,
            url: errorMeta.url,
          },
        },
      );
    },
    onUploadSuccess: (s3UploadTime) =>
      trackVideaNotesEvent(mixpanelEvents.videaNotesS3UploadTime, {
        s3UploadTime,
        uploadFlow: 'synapse',
      }),
  });

  // Keep ref in sync with startAmbientSession
  startAmbientSessionRef.current = startAmbientSession;

  // Use synapse flow when configured
  const shouldUseSynapseFlow = isSynapseConfigured;
  const processingError = shouldUseSynapseFlow
    ? synapseProcessingError
    : legacyProcessingError;

  // Unified upload function that chooses the right flow
  const uploadAudio = useCallback(
    async (overrideBlobUrl?: string) => {
      if (shouldUseSynapseFlow) {
        setNoteGenerationStep('preparing');
        setUiStatus(VideaNotesUIState.NOTE_GENERATION);
        if (overrideBlobUrl) {
          // File upload: create session + upload blob as single chunk
          await synapseUploadFile(overrideBlobUrl);
        } else {
          // Recording: chunks already uploaded, just complete the session
          await synapseFinishRecording();
        }
      } else {
        setNoteGenerationStep('preparing');
        setUiStatus(VideaNotesUIState.NOTE_GENERATION);
        // Legacy flow: upload the full blob
        await legacyUploadAudio(overrideBlobUrl);
      }
    },
    [
      shouldUseSynapseFlow,
      synapseFinishRecording,
      synapseUploadFile,
      legacyUploadAudio,
      setUiStatus,
      setNoteGenerationStep,
    ],
  );

  // PMS save hook
  const { t } = useLingui();
  const {
    triggerSaveNoteToPMS,
    isSavingNoteToPMS,
    providerDialogState,
    handleProviderConfirm,
    handleProviderCancel,
  } = useSaveNoteToPMS({
    currentOrganizationId,
    currentPracticeId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    selectedNoteId: selectedNote?.id ?? undefined,
    noteContent,
    noteWithTemplateData: selectedNote,
    editedFields,
    hasWritebackEnabled,
    onWritebackPrecheckFailed: (reason) => {
      trackVideaNotesEvent(mixpanelEvents.writebackPrecheckFailed, { reason });
      const description =
        reason === 'NO_PMS_PATIENT'
          ? t`No matching patient found in your PMS. Please ensure the patient exists before saving.`
          : t`No provider is available for this note. Please contact support.`;
      toast(
        <Toast
          variant="error"
          title={t`Unable to save to chart`}
          description={description}
        />,
        { closeButton: true, position: 'top-right', style: { width: '460px' } },
      );
    },
    onProviderSelected: (providerPmsId) => {
      trackVideaNotesEvent(mixpanelEvents.writebackProviderSelected, {
        providerPmsId,
      });
    },
    onProviderDialogCancelled: () => {
      trackVideaNotesEvent(mixpanelEvents.writebackProviderDialogCancelled);
    },
  });

  // Single "saving to PMS in progress" state: mutation in flight OR note data says writeback processing
  const isSavingToPMSInProgress = isSavingNoteToPMS || isNoteBeingSavedToPMS;

  // Determine if content should be shown
  const shouldShowContent =
    !isLoadingNotes || (!unifiedAppointmentId && !standalonePatientVisitId);

  // Event handlers hook
  const {
    showCopyTooltip,
    handleSheetOpen,
    handlePatientCreated,
    handleGenerateNote,
    handleCreateAdditionalNote,
    handleCancelRecording,
    handleCancelRecordingConfirmation,
    handlePause,
    handleResume,
    handleSaveNoteToPMS,
    handleRetryWritebackToPMS,
    handleSummaryTextAreaClick,
    handleCopyText,
    handleCloseSheet,
    handleSheetOpenChange,
  } = useVideaNotesEventHandlers({
    setAppointmentDataForVideaNotes,
    setUiStatus,
    setIsSheetOpen,
    setIsCancelRecordingConfirmationShown,
    uiStatus,
    isCancelRecordingConfirmationShown,
    patient,
    appointmentStartsAt,
    estimatedVisitId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    username,
    trackVideaNotesEvent,
    uploadAudio,
    triggerSaveNoteToPMS,
    triggerRetryWriteback: triggerSaveNoteToPMS, // Same operation as initial save
    handleSaveNoteBeforeClose,
  });

  // Context value hooks
  const coreContextValue = useCoreContextValue({
    uiStatus,
    voiceNotesSource,
    patientName,
    patient,
    standalonePatientVisitId,
    isVoiceNotesConsentEnabled: preferences?.enableVoiceNotesConsent ?? false,
    processingError,
    onCloseSheet: handleCloseSheet,
    onPatientCreated: handlePatientCreated,
  });

  const recordingContextValue = useRecordingContextValue({
    recorderStatus,
    mediaBlobUrl,
    templateOptions,
    selectedTemplate,
    selectedTemplateName,
    selectedTemplateUserInstructions,
    supportedLanguages,
    selectedLanguage,
    onSelectedLanguage: setSelectedLanguage,
    onSelectedTemplate: setSelectedTemplate,
    isAudioUploadEnabled: true,
    showDevTools: showDevtools,
    onMonitoringStreamChange: setMonitoringStream,
    onCancelRecording: handleCancelRecording,
    onCancelRecordingConfirmation: handleCancelRecordingConfirmation,
    onPauseRecording: handlePause,
    onResumeRecording: handleResume,
    onStartRecording: handleStartRecordingConsent,
    onStartProviderOnlyRecording: handleStartProviderOnlyRecordingConsent,
    onGenerateNote: handleGenerateNote,
    onPatientConsentDenied: handlePatientConsentDeniedConsent,
    onPermissionOutcome: (outcome) =>
      trackVideaNotesEvent(
        mixpanelEvents.videaNotesMicrophonePermissionOutcome,
        { outcome },
      ),
  });

  const noteResultContextValue = useNoteResultContextValue({
    clinicalNotesData,
    selectedNote,
    noteContent,
    patientConsentBlurb,
    isNoteReadOnly: isNoteReadOnly || isSavingToPMSInProgress,
    isNoteSavedToPMS,
    isSavingToPMSInProgress,
    isSavingNoteUpdate,
    isRetryingWriteback: isSavingToPMSInProgress,
    hasWritebackEnabled,
    hasBeenEdited,
    showCopyTooltip,
    variableDefinitions,
    onNoteChange: handleSetNoteContent,
    onNoteClick: handleSummaryTextAreaClick,
    onSelectedNote: handleSetSelectedNoteId,
    onCopyText: handleCopyText,
    onCreateAdditionalNote: handleCreateAdditionalNote,
    onSaveNoteToPMS: handleSaveNoteToPMS,
    onRetryWritebackToPMS: handleRetryWritebackToPMS,
    onResetNote: handleResetNoteToOriginal,
    onVariableValueChange: handleVariableValueChange,
  });

  // Don't render if feature is disabled
  if (!isVideaNotesEnabled) {
    return null;
  }

  return (
    <>
      {/* Keyframes for the red pulsing inset glow on early silence */}
      <style>{`
        @keyframes pulse-danger-inset {
          0%, 100% { box-shadow: inset 0 0 30px 4px rgba(239,68,68,0.25); }
          50% { box-shadow: inset 0 0 60px 10px rgba(239,68,68,0.45); }
        }
      `}</style>
      {/* Recovery modal for interrupted recordings */}
      <RecordingRecoveryModal
        onShow={(session) =>
          trackVideaNotesEvent(mixpanelEvents.videaNotesRecoveryShown, {
            recordingDurationChunks: session.chunkCount,
            recordingAge: Date.now() - session.createdAt,
          })
        }
        onResume={(session) => {
          trackVideaNotesEvent(mixpanelEvents.videaNotesRecoveryResumeClick, {
            recordingDurationChunks: session.chunkCount,
            recordingAge: Date.now() - session.createdAt,
          });
          // Open the sheet when resuming
          setIsSheetOpen(true);
          // Restore appointment context from recovery session
          // This prevents losing patient name, IDs, etc. after page refresh
          const recoveredFirstName = session.patientFirstName ?? '';
          const recoveredLastName = session.patientLastName ?? '';
          setAppointmentDataForVideaNotes({
            patient: {
              firstName: recoveredFirstName || undefined,
              lastName: recoveredLastName || undefined,
              name:
                `${recoveredFirstName} ${recoveredLastName}`.trim() ||
                undefined,
              videaId: session.patientVideaId,
            },
            standalonePatientVisitId: session.standalonePatientVisitId,
            unifiedId: session.unifiedAppointmentId,
            estimatedVisitId: null,
          });
          // Restore template selection so the UI dropdown matches what was
          // originally selected before the page refresh
          if (session.templateId) {
            setSelectedTemplate(session.templateId);
          }
          debug.log('Resuming session:', session.id);
          // Start Synapse ambient session for the recovered recording.
          // IMPORTANT: startAmbientSessionRef.current fires before the atom write
          // above re-renders, so props (unifiedAppointmentId, etc.) are still undefined.
          // This is intentional — the OPFS fallback in startAmbientSession recovers
          // externalPatientId, pmsPatientId, and imagingPatientId which are NOT available
          // on PendingRecoverySession and thus not in the atom write above.
          debug.log(
            'Starting ambient session for recovered recording',
            session.ambientSessionId
              ? `(reusing session ${session.ambientSessionId})`
              : '(creating new session)',
          );
          startAmbientSessionRef
            .current({
              opfsSessionId: session.id,
              recoveredAmbientSessionId: session.ambientSessionId,
            })
            .catch((error) => {
              debug.error(
                'Failed to start ambient session for recovery:',
                error,
              );
            });
        }}
        onDiscard={() => {
          trackVideaNotesEvent(
            mixpanelEvents.videaNotesRecoveryDiscardClick,
          );
          debug.log('Discarded interrupted recording');
        }}
      />
      <ProviderSelectionDialog
        open={providerDialogState.open}
        providers={providerDialogState.providers}
        onConfirm={handleProviderConfirm}
        onCancel={handleProviderCancel}
      />
      <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          onOpenAutoFocus={handleSheetOpen}
          className="vai-max-h-screen vai-flex vai-flex-col !vai-w-[60vw] vai-min-w-[min(800px,calc(100vw-2rem))] vai-max-w-[calc(100vw-2rem)] vai-font-inter vai-overflow-hidden !vai-border-0"
          closeButtonClassName="vai-text-primary-foreground"
          aria-describedby=""
          data-testid="videa-notes-sheet"
        >
          {/* Inset red pulsing glow overlay for early silence — separate div to avoid re-triggering Radix sheet animations */}
          {silenceSeverity === 'early' && (
            <div
              className="vai-absolute vai-inset-0 vai-pointer-events-none vai-z-[1] vai-rounded-[inherit]"
              style={{ animation: 'pulse-danger-inset 2s ease-in-out infinite' }}
            />
          )}
          <SheetHeader className="vai-flex vai-flex-row vai-justify-start vai-bg-navy-500 vai-px-[24px] vai-py-3 !vai-text-left vai-text-primary-foreground">
            <SheetTitle className="vai-font-inter vai-flex vai-items-center vai-gap-4 vai-text-[16px] vai-font-semibold vai-text-primary-foreground vai-w-fit vai-flex-shrink-0">
              <FontAwesomeIcon
                icon={faWaveformLines}
                className="vai-text-aqua-500"
              />
              <Trans>Voice Note</Trans>
            </SheetTitle>
          </SheetHeader>

          <div className="vai-flex-1 vai-px-6 vai-py-4 vai-font-inter vai-flex vai-flex-col vai-min-h-0 vai-max-h-full vai-overflow-hidden">
            {/* Loading state */}
            {isLoadingNotes &&
              (unifiedAppointmentId || standalonePatientVisitId) && (
                <p>Loading notes...</p>
              )}

            {/* Error state */}
            {axios.isAxiosError(notesError) && (
              <p className="text-destructive">
                {notesError.response?.data?.message ?? 'An error occurred'}
              </p>
            )}

            {/* Main content with context providers */}
            {shouldShowContent && (
              <VideaNotesCoreProvider value={coreContextValue}>
                <VideaNotesRecordingProvider value={recordingContextValue}>
                  <VideaNotesNoteResultProvider value={noteResultContextValue}>
                    <VideaNotesMainContent />
                  </VideaNotesNoteResultProvider>
                </VideaNotesRecordingProvider>
              </VideaNotesCoreProvider>
            )}
          </div>
          <Toaster position="top-right" />
        </SheetContent>
      </Sheet>
    </>
  );
};
